import { useCallback } from "react";

import * as Sentry from "@sentry/browser";
import { useMutation, useRelayEnvironment } from "react-relay";

import {
  AddPolicyCollectionMutation,
  invalidatePolicyLists,
} from "app/mutations";

import type {
  AddPolicyCollectionInput,
  AddPolicyCollectionMutation as AddPolicyCollectionMutationType,
  AddPolicyCollectionMutation$data,
} from "app/mutations/__generated__/AddPolicyCollectionMutation.graphql";
import type { Disposable } from "react-relay";
import type { PayloadError } from "relay-runtime";

interface Params {
  input: AddPolicyCollectionInput;
  connections: string[];
  onCompleted?: (
    response: AddPolicyCollectionMutation$data,
    errors: PayloadError[],
  ) => void;
  onErrors?: (errors: string[]) => void;
}

export function useAddPolicyCollection(): [
  (
    params: Params,
    onCompleted: (
      response: AddPolicyCollectionMutation$data,
      errors: PayloadError[] | null,
    ) => void,
  ) => Disposable,
  boolean,
] {
  const [commit, isInFlight] = useMutation<AddPolicyCollectionMutationType>(
    AddPolicyCollectionMutation,
  );
  const environment = useRelayEnvironment();

  return [
    useCallback(
      (
        params: Params,
        onCompleted: (
          response: AddPolicyCollectionMutation$data,
          errors: PayloadError[] | null,
        ) => void,
      ) => {
        return commit({
          variables: {
            input: params.input,
            connections: params.connections,
          },
          onCompleted: (response, errors) => onCompleted(response, errors),
          updater: (store) => invalidatePolicyLists(environment, store),
          onError: (err) => {
            Sentry.captureException(err);
          },
        });
      },
      [commit, environment],
    ),
    isInFlight,
  ];
}
